import React from "react";
import { Global } from "@emotion/react";
import { useLocation, useParams } from "react-router-dom";
import { useOne } from "src/system/useOne.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import { useErpTranslation } from "src/system/useErpTranslation.ts";
import { intakeProgress } from "src/admin/modules/order-return/intake/useIntakeQueue.ts";
import { isTerminalReturnState } from "src/admin/modules/order-return/constants.ts";
import type { OrderReturnCardData } from "src/admin/modules/order-return/forms/order-return/types.ts";
import * as styles from "src/admin/modules/order-return/components/ReturnStepTitle.styles.ts";

/**
 * Шаг в ленте: номер, название, подпись с состоянием.
 *
 * Лента — это и есть вкладки карточки, только нарисованные лентой. Второй
 * навигации не заводим: вкладки уже умеют дочерние маршруты, глубокую ссылку,
 * кнопку «назад» и недоступность с подсказкой причины. Рисуется это через
 * заголовок вкладки, который система принимает узлом, — системный слой не
 * трогается вовсе.
 *
 * Подпись обязательна у каждого шага. Без неё лента из трёх названий не
 * говорит ничего: номер шага не отвечает ни «сколько осталось», ни «почему
 * заперто», а именно за этим на неё и смотрят.
 */
export type ReturnStep = 1 | 2 | 3;

export const ReturnStepTitle = ({ step }: { step: ReturnStep }) => {
  const params = useParams();
  const { pathname } = useLocation();
  const { t } = useErpTranslation({
    resource: ResourceEnum.OrderReturn,
    type: "layout",
  });

  /*
    Тот же ключ запроса, что у карточки, — и запрос остаётся один.

    Лента читает тот же возврат, что и форма. Пока ключи расходились
    включениями, карточка грузила его дважды — и оба раза целиком, потому
    что эндпоинт отдаёт связи независимо от `include`. Включений нет
    нигде: ни здесь, ни в конфигурации шагов.
  */
  const { data } = useOne<OrderReturnCardData>({
    resource: ResourceEnum.OrderReturn,
    id: params.id!,
    queryOptions: { enabled: !!params.id },
  });

  const card = data?.data;
  const items = card?.items ?? [];

  const name = t(`steps.${step}.name`);
  const state = stateOf(step, card, items);

  // Синим горит только шаг, на котором стоим. Иначе синих кружков два:
  // «идёт» — это состояние работы, а не «вы здесь», и лента с двумя
  // одинаково яркими номерами не отвечает на вопрос, где я нахожусь.
  const kind =
    state.kind === "now" && !isHere(pathname, step) ? "open" : state.kind;

  return (
    <>
      {/*
        Полоса вкладок в этом модуле — лента шагов. Правило ставится один
        раз, с первого шага: три одинаковых набора стилей в документе ничего
        не добавляют.
      */}
      {step === 1 && <Global styles={styles.ribbon} />}

      <span
        css={styles.step(kind)}
        data-return-step={kind}
        {...(isHere(pathname, step) ? { "data-return-step-here": "" } : {})}
      >
        <span css={styles.mark(kind)}>{kind === "done" ? "✓" : step}</span>
        <span css={styles.text} data-step-text={""}>
          <span css={styles.name}>{name}</span>
          <span css={styles.sub}>{t(state.sub, state.values)}</span>
        </span>
        {kind === "locked" && (
          <span css={styles.lock}>{t("steps.locked")}</span>
        )}
      </span>
    </>
  );
};

/**
 * На каком шаге стоим.
 *
 * По адресу, а не по признаку от системы: заголовок вкладки о своей
 * активности не знает, а полоса вкладок и так её подсвечивает — здесь нужен
 * только цвет кружка.
 */
const isHere = (pathname: string, step: ReturnStep): boolean => {
  const tail = pathname.replace(/\/+$/, "").split("/").pop() ?? "";

  if (step === 2) {
    return tail === "wareneingang";
  }

  if (step === 3) {
    return tail === "entscheidung";
  }

  return tail !== "wareneingang" && tail !== "entscheidung";
};

type StepState = {
  kind: "done" | "now" | "open" | "locked";
  sub: string;
  values?: Record<string, number | string>;
};

/**
 * Четыре состояния шага.
 *
 * «Сделан» и «заперт» считает сервер — гейт шагов отвечает ими в карточке.
 * Экран их не выводит: правило «решение только после приёмки» живёт в одном
 * месте, и второе его исчисление здесь разошлось бы с первым на первой же
 * правке.
 */
const stateOf = (
  step: ReturnStep,
  card: OrderReturnCardData | undefined,
  items: OrderReturnCardData["items"],
): StepState => {
  if (card === undefined) {
    return { kind: "open", sub: "steps.loading" };
  }

  // Приёмка началась — значит заявка позади, какой бы она ни была. Это и
  // есть граница шага ①: его закрывает переход к коробке, а не галочка.
  const intakeBegun =
    card.received_at !== null ||
    isTerminalReturnState(card.state) ||
    items.some((item) => item.intake_result !== "pending");

  if (step === 1) {
    // Считаем отмеченное, а не заведённое: строки создаёт заведение возврата
    // на весь заказ, а работой их делает отметка оператора. «10 Positionen
    // erfasst» над пустой таблицей без единой галочки говорило обратное.
    //
    // Части набора не в счёт: их заводит приёмка, и на шаге ① их не видно.
    const listed = items.filter((item) => item.set_parent_item_id === null);
    const picked = listed.filter((item) => item.expectation === "expected");

    // Галочка — только когда шаг позади. Пока оператор на звонке и отметил
    // одну строку из пяти, «✓ сделано» — самый громкий сигнал на экране, и
    // он врёт.
    if (intakeBegun) {
      /*
        Ни одной заявленной позиции — значит разговора с клиентом не было:
        возврат пришёл импортом из ERP1 или заявкой из канала. «0 von 4
        gewählt» рядом с зелёной галочкой читается как «ничего не сделано»,
        хотя шаг позади и выбирать было нечего.
      */
      if (picked.length === 0) {
        return { kind: "done", sub: "steps.1.unannounced" };
      }

      return {
        kind: "done",
        sub: "steps.1.done",
        values: { count: picked.length, total: listed.length },
      };
    }

    if (picked.length === 0) {
      return { kind: "now", sub: "steps.1.empty" };
    }

    return {
      kind: "now",
      sub: "steps.1.done",
      values: { count: picked.length, total: listed.length },
    };
  }

  if (step === 2) {
    // Счёт один на ленту, список и телефон — и он в штуках: «von N Stück»
    // над числом строк было бы прямой неправдой на каждой позиции с
    // количеством.
    const progress = intakeProgress(items as never);

    if (card.received_at !== null || isTerminalReturnState(card.state)) {
      /*
        Приёмка закрыта, но работа вернулась: пришла вторая коробка или
        серую позицию вернули в работу после завершения.

        Говорим «wieder offen», а не «abgeschlossen»: иначе лента уверяет,
        что разбирать нечего, пока в списке лежит неразобранная вещь. И не
        «идёт» — это не откат, коробку уже закрывали, и подпись должна
        сказать об этом словом.
      */
      if (
        !isTerminalReturnState(card.state) &&
        progress.done < progress.total
      ) {
        return {
          kind: "now",
          sub: "steps.2.reopened",
          values: { count: progress.total - progress.done },
        };
      }

      return { kind: "done", sub: "steps.2.done" };
    }

    if (card.steps?.intake_open === false) {
      return { kind: "locked", sub: "steps.2.locked" };
    }

    return { kind: "now", sub: "steps.2.progress", values: progress };
  }

  if (card.steps?.decision_open === false) {
    return { kind: "locked", sub: "steps.3.locked" };
  }

  // Замок снят, но коробку ещё не открывали — решать не по чему. «3 offen»
  // над нетронутым возвратом обещает работу, которой нет: решения считаются
  // по строкам, а строки пока ничего не прошли. Исключения замка (импорт из
  // ERP1, закрытие без приёмки) попадают сюда же.
  if (!intakeBegun) {
    return { kind: "open", sub: "steps.3.locked" };
  }

  /*
    Части набора решений не имеют — и в счёт не идут.

    Деньги живут на строке-наборе: в таблице решений частей нет вовсе, и
    решить по ним нечего. Пока они считались наравне, набор из четырёх
    частей добавлял в «offen» четыре несуществующих решения — экран обещал
    работу, которой нет, и после последнего решения счётчик не обнулялся.
  */
  const decidable = items.filter((item) => item.set_parent_item_id === null);

  const executed = decidable.filter(
    (item) => item.decision_executed_at !== null,
  );
  const pending = decidable.filter(
    (item) =>
      item.decision_executed_at === null && item.intake_result !== "no_intake",
  );

  /*
    «offen» раньше означало сразу две разные вещи: «решения ещё нет» и
    «решение есть, но не исполнено». Для оператора это разная работа —
    в первом случае надо решать, во втором нажать кнопку исполнения, — а
    счётчик называл их одним словом и одним числом.

    Теперь «offen» означает только первое, а решённое и неисполненное
    считается отдельно как «bereit».
  */
  const open = pending.filter((item) => item.decision === "none");
  const ready = pending.filter((item) => item.decision !== "none");

  if (pending.length === 0 && executed.length > 0) {
    return { kind: "done", sub: "steps.3.done" };
  }

  return {
    kind: "now",
    sub: "steps.3.progress",
    values: {
      open: open.length,
      ready: ready.length,
      executed: executed.length,
    },
  };
};
